/**
 * Invoices API Client
 * Handles all invoice and client CRUD operations with subscription limit checks
 */
import { apiClient } from './client';
import {
  Invoice,
  InvoiceCreateData,
  InvoiceUpdateData,
  Client,
  ClientCreateData,
  InvoiceUsage,
  InvoiceStatus,
  InvoiceDirection,
  InvoiceType,
  PaymentMethod,
  DashboardStats
} from '../types';

// ==================== Client API ====================

/**
 * Get all clients for the current user
 */
export const getClients = async (
  activeOnly: boolean = true,
  favoritesFirst: boolean = true,
  search?: string
): Promise<Client[]> => {
  const params = new URLSearchParams();
  params.append('active_only', String(activeOnly));
  params.append('favorites_first', String(favoritesFirst));
  if (search) {
    params.append('search', search);
  }
  const response = await apiClient.get<Client[]>(`/api/clients/?${params.toString()}`);
  return response.data;
};

/**
 * Get favorite clients only
 */
export const getFavoriteClients = async (): Promise<Client[]> => {
  const response = await apiClient.get<Client[]>('/api/clients/favorites');
  return response.data;
};

/**
 * Get a specific client by ID
 */
export const getClient = async (clientId: number): Promise<Client> => {
  const response = await apiClient.get<Client>(`/api/clients/${clientId}`);
  return response.data;
};

/**
 * Create a new client
 */
export const createClient = async (data: ClientCreateData): Promise<Client> => {
  const response = await apiClient.post<Client>('/api/clients/', data);
  return response.data;
};

/**
 * Update an existing client
 */
export const updateClient = async (clientId: number, data: Partial<ClientCreateData>): Promise<Client> => {
  const response = await apiClient.put<Client>(`/api/clients/${clientId}`, data);
  return response.data;
};

/**
 * Toggle client favorite status
 */
export const toggleClientFavorite = async (clientId: number): Promise<Client> => {
  const response = await apiClient.post<Client>(`/api/clients/${clientId}/favorite`);
  return response.data;
};

/**
 * Delete a client (soft delete by default)
 */
export const deleteClient = async (clientId: number, permanent: boolean = false): Promise<void> => {
  await apiClient.delete(`/api/clients/${clientId}?permanent=${permanent}`);
};

// ==================== Invoice API ====================

/**
 * Get all invoices for the current user
 */
export const getInvoices = async (
  status?: InvoiceStatus,
  invoiceType?: InvoiceType,
  clientId?: number,
  sellerId?: number
): Promise<Invoice[]> => {
  const params = new URLSearchParams();
  if (status) {
    params.append('invoice_status', status);
  }
  if (invoiceType) {
    params.append('invoice_type', invoiceType);
  }
  if (clientId) {
    params.append('client_id', String(clientId));
  }
  if (sellerId) {
    params.append('seller_id', String(sellerId));
  }
  const response = await apiClient.get<Invoice[]>(`/api/invoices/?${params.toString()}`);
  return response.data;
};

/**
 * Get the next invoice number
 */
export const getNextInvoiceNumber = async (): Promise<string> => {
  const response = await apiClient.get<{ next_number: string }>('/api/invoices/next-number');
  return response.data.next_number;
};

/**
 * Get invoice usage stats
 */
export const getInvoiceUsage = async (): Promise<InvoiceUsage> => {
  const response = await apiClient.get<InvoiceUsage>('/api/invoices/usage');
  return response.data;
};

/**
 * Get a specific invoice by ID
 */
export const getInvoice = async (invoiceId: number): Promise<Invoice> => {
  const response = await apiClient.get<Invoice>(`/api/invoices/${invoiceId}`);
  return response.data;
};

/**
 * Create a new invoice
 * NOTE: This counts against the invoice creation limit
 */
export const createInvoice = async (data: InvoiceCreateData): Promise<Invoice> => {
  const response = await apiClient.post<Invoice>('/api/invoices/', data);
  return response.data;
};

/**
 * Update an existing invoice
 * NOTE: This counts against the invoice edit limit
 */
export const updateInvoice = async (invoiceId: number, data: InvoiceUpdateData): Promise<Invoice> => {
  const response = await apiClient.put<Invoice>(`/api/invoices/${invoiceId}`, data);
  return response.data;
};

/**
 * Delete an invoice
 */
export const deleteInvoice = async (invoiceId: number): Promise<void> => {
  await apiClient.delete(`/api/invoices/${invoiceId}`);
};

/**
 * Generate PDF for an invoice
 */
export const generateInvoicePdf = async (invoiceId: number): Promise<{ pdf_path: string }> => {
  const response = await apiClient.post<{ pdf_path: string }>(`/api/invoices/${invoiceId}/generate-pdf`);
  return response.data;
};

/**
 * Download invoice PDF
 * NOTE: This counts against the PDF download limit
 */
export const downloadInvoicePdf = async (invoiceId: number): Promise<Blob> => {
  const response = await apiClient.get(`/api/invoices/${invoiceId}/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Mark invoice as sent
 */
export const markInvoiceSent = async (invoiceId: number): Promise<Invoice> => {
  const response = await apiClient.post<Invoice>(`/api/invoices/${invoiceId}/mark-sent`);
  return response.data;
};

/**
 * Mark invoice as fully paid
 */
export const markInvoicePaid = async (invoiceId: number): Promise<Invoice> => {
  const response = await apiClient.post<Invoice>(`/api/invoices/${invoiceId}/mark-paid`);
  return response.data;
};

/**
 * Record a payment (supports partial payments)
 */
export const recordPayment = async (
  invoiceId: number,
  amount: number,
  paymentMethod?: PaymentMethod
): Promise<Invoice> => {
  const response = await apiClient.post<Invoice>(`/api/invoices/${invoiceId}/record-payment`, {
    amount,
    payment_method: paymentMethod
  });
  return response.data;
};

/**
 * Mark invoice as ready to process
 */
export const markInvoiceReady = async (invoiceId: number): Promise<Invoice> => {
  const response = await apiClient.post<Invoice>(`/api/invoices/${invoiceId}/mark-ready`);
  return response.data;
};

/**
 * Mark invoice as confirmed
 */
export const markInvoiceConfirmed = async (invoiceId: number): Promise<Invoice> => {
  const response = await apiClient.post<Invoice>(`/api/invoices/${invoiceId}/mark-confirmed`);
  return response.data;
};

/**
 * Get dashboard statistics
 */
export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await apiClient.get<DashboardStats>('/api/invoices/dashboard/stats');
  return response.data;
};

// ==================== Helper Functions ====================

/**
 * Download PDF and trigger browser download
 */
export const downloadAndSavePdf = async (invoiceId: number, invoiceNumber: string): Promise<void> => {
  const blob = await downloadInvoicePdf(invoiceId);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoice_${invoiceNumber.replace(/[/\\]/g, '-')}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download KSeF FA(3) XML for an invoice
 */
export const downloadInvoiceXml = async (invoiceId: number): Promise<Blob> => {
  const response = await apiClient.get(`/api/invoices/${invoiceId}/xml`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Download KSeF XML and trigger browser save dialog
 */
export const downloadAndSaveXml = async (invoiceId: number, invoiceNumber: string): Promise<void> => {
  const blob = await downloadInvoiceXml(invoiceId);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoiceNumber.replace(/[/\\]/g, '-')}_ksef.xml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Check if user can create an invoice
 */
export const canCreateInvoice = (usage: InvoiceUsage): boolean => {
  return usage.invoices_created < usage.invoices_created_limit;
};

/**
 * Check if user can edit an invoice
 */
export const canEditInvoice = (usage: InvoiceUsage): boolean => {
  return usage.invoices_edited < usage.invoices_edited_limit;
};

/**
 * Check if user can download a PDF
 */
export const canDownloadPdf = (usage: InvoiceUsage): boolean => {
  return usage.invoice_downloads < usage.invoice_downloads_limit;
};

/**
 * Get invoice type display label
 */
export const getInvoiceTypeLabel = (type: InvoiceType): string => {
  const labels: Record<InvoiceType, string> = {
    standard: 'Invoice',
    vat: 'VAT Invoice',
    reverse_charge: 'Reverse Charge',
    proforma: 'Proforma',
    credit_note: 'Credit Note'
  };
  return labels[type] || type;
};

/**
 * Get payment method display label
 */
export const getPaymentMethodLabel = (method: PaymentMethod): string => {
  const labels: Record<PaymentMethod, string> = {
    transfer: 'Bank Transfer',
    cash: 'Cash',
    card: 'Card',
    paypal: 'PayPal',
    other: 'Other'
  };
  return labels[method] || method;
};

/**
 * Format client address as single line
 */
export const formatClientAddress = (client: Client): string => {
  const parts: string[] = [];

  if (client.street) {
    let streetLine = client.street;
    if (client.street_number) {
      streetLine += ` ${client.street_number}`;
    }
    parts.push(streetLine);
  }

  if (client.city) {
    parts.push(client.city);
  }

  if (client.country) {
    parts.push(client.country);
  }

  return parts.join(', ');
};

/**
 * Get invoice status display label, adjusted for incoming vs outgoing direction.
 * Incoming invoices use different labels: ready_to_process="Received", confirmed="Approved to Pay".
 */
export const getInvoiceStatusLabel = (status: InvoiceStatus, direction?: InvoiceDirection): string => {
  if (direction === 'incoming') {
    const incomingLabels: Partial<Record<InvoiceStatus, string>> = {
      ready_to_process: 'Received',
      confirmed: 'Approved to Pay',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled'
    };
    if (incomingLabels[status]) return incomingLabels[status]!;
  }
  const labels: Record<InvoiceStatus, string> = {
    ready_to_process: 'Ready to Process',
    confirmed: 'Confirmed',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled'
  };
  return labels[status] || status;
};

/**
 * Get invoice status color classes
 */
export const getInvoiceStatusColor = (status: InvoiceStatus): string => {
  const colors: Record<InvoiceStatus, string> = {
    ready_to_process: 'bg-blue-500/20 text-blue-300',
    confirmed: 'bg-coral-500/20 text-coral-300',
    sent: 'bg-yellow-500/20 text-yellow-300',
    paid: 'bg-green-500/20 text-green-300',
    overdue: 'bg-red-500/20 text-red-300',
    cancelled: 'bg-gray-500/20 text-gray-300'
  };
  return colors[status] || 'bg-coral-500/20 text-coral-300';
};

// Export all functions as an object for convenience
export const invoiceApi = {
  // Clients
  getClients,
  getFavoriteClients,
  getClient,
  createClient,
  updateClient,
  toggleClientFavorite,
  deleteClient,
  // Invoices
  getInvoices,
  getNextInvoiceNumber,
  getInvoiceUsage,
  getInvoice,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  generateInvoicePdf,
  downloadInvoicePdf,
  markInvoiceSent,
  markInvoicePaid,
  markInvoiceReady,
  markInvoiceConfirmed,
  recordPayment,
  getDashboardStats,
  // Helpers
  downloadAndSavePdf,
  canCreateInvoice,
  canEditInvoice,
  canDownloadPdf,
  getInvoiceTypeLabel,
  getInvoiceStatusLabel,
  getInvoiceStatusColor,
  getPaymentMethodLabel,
  formatClientAddress
};

export default invoiceApi;
